'use client'
import { signOut } from "next-auth/react"
import { useSession } from "@/context/SessionContext"

import Link from "next/link"
import Image from "next/image"
import { useState, useEffect, useRef } from "react"
import { usePathname } from "next/navigation"
import { MaxWidthWrapper } from "./max-width-wrapper"
import { Dialog } from "@headlessui/react"
import { useCart } from "@/context/CartContext"
import { useSimplePet } from "@/context/SimplePetContext"
import { Phone, X } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import TrackModal from "./track-modal"
import { usePetNavigation } from "@/hooks/usePetNavigation"
import LoadingSpinner from "./LoadingSpinner"

interface HeaderProps {
  /**
   * `simple`: the owner-facing home page — the sections an owner wants
   * (prices, how it works, questions) plus a call button; clinic and blog
   * links are secondary. `full` (default): everything, including the
   * clinic/logged-in items.
   */
  variant?: "full" | "simple"
}

// Owner-facing nav for the home page. Anchors are absolute so the links work
// from any page that uses the simple header.
const SIMPLE_LINKS = [
  { label: "Prices", href: "/#pricing" },
  { label: "How it works", href: "/#how" },
  { label: "Questions", href: "/#faq" },
  { label: "For vet clinics", href: "/clinics" },
]
const SIMPLE_SECONDARY_LINKS = [
  { label: "For vet clinics", href: "/clinics" },
  { label: "Blog", href: "/blog" },
  { label: "Contact us", href: "/contact" },
]
const PHONE_DISPLAY = "027 299 3693"
const PHONE_TEL = "+64272993693"

export default function Header({ variant = "full" }: HeaderProps) {
  const simple = variant === "simple"
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const pathname = usePathname()
  const toggleMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen)
  const closeMenu = () => setIsMobileMenuOpen(false)

  // Home-page section links from the mobile menu: the open menu locks page
  // scrolling, so close it first and scroll once the lock is released.
  const goToSection = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    const hash = href.slice(href.indexOf("#"))
    const target = pathname === "/" ? document.querySelector(hash) : null
    if (!target) return closeMenu() // off the home page: let the link navigate
    e.preventDefault()
    closeMenu()
    // After React has re-rendered and removed the body scroll lock.
    setTimeout(() => {
      target.scrollIntoView({ block: "start" })
      history.replaceState(null, "", hash)
    }, 50)
  }

  const [isSticky, setIsSticky] = useState(false)
  const { session, status, update } = useSession()
  const userName = session?.user?.name || session?.user?.email || "User"
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { navigateToHome, navigateWithPet, hasPet } = usePetNavigation()
  const { getCartItemCount } = useCart()
  const { currentPet, setCurrentPet, clearCurrentPet } = useSimplePet()
  const [isTrackModalOpen, setIsTrackModalOpen] = useState(false)
  const { toast } = useToast()
  const [sessionTimeout, setSessionTimeout] = useState(false)

  // Removed problematic session update effect that was causing loops

  // Add timeout for session loading
  useEffect(() => {
    if (status === "loading") {
      const timeout = setTimeout(() => {
        setSessionTimeout(true)
      }, 5000) // 5 second timeout
      
      return () => clearTimeout(timeout)
    } else {
      setSessionTimeout(false)
    }
  }, [status])

  // Removed cleanup effect as it's now handled in the custom hook

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdown])

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 0)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // 🔁 useEffect to handle class on body
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add("mobile-menu-open")
    } else {
      document.body.classList.remove("mobile-menu-open")
    }

    return () => {
      // Clean up on unmount
      document.body.classList.remove("mobile-menu-open")
    }
  }, [isMobileMenuOpen])

  const handleLogout = async () => {
    try {
      setShowDropdown(false)
      closeMenu()
      await signOut({ 
        redirect: true,
        callbackUrl: '/' 
      })
    } catch (error) {
      console.error('Sign out error:', error)
      // Fallback: force redirect to home
      window.location.href = '/'
    }
  }

  // Don't show pet linking section on home page, process page, or dashboard pages (for veterinarians/admins)
  const showPetSection = pathname !== "/" && pathname !== "/process" && !pathname?.startsWith("/dashboard")

  // Logged-out visitors get the public education page (CE, podcast, lunch & learns,
  // events); logged-in clinic/admin users keep the real training-courses dashboard.
  const trainingHref = session ? "/training" : "/learning"

  // Resources is a clinic/admin tool — only surface it inside the dashboard/admin areas.
  const showResources =
    pathname?.startsWith("/clinicDashboard") ||
    pathname?.startsWith("/dashboard") ||
    pathname?.startsWith("/admin")

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-[99] bg-white py-[30px] ${isSticky ? '!py-[15px]' : ''} ${currentPet ? 'pb-4' : ''}`}>
        <div className="container">
          <MaxWidthWrapper>
            <div className="flex justify-between items-center">
              <button onClick={navigateToHome} className="relative cursor-pointer">
                <Image src="/icons/logo-rgb.svg" alt="PetAftercare" className="w-[250px] md:w-[200px] h-[60px] md:h-[50px] sm:h-auto" width={250} height={60} priority />
              </button>

              {/* Burger icon for mobile */}
              <div className="hidden sm:flex absolute top-[24px] right-[20px]">
                <button
                  onClick={toggleMenu}
                  className={`text-[#002e65] z-[99] text-3xl menu-button ${isMobileMenuOpen ? 'active' : ''}`}
                >
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                </button>
              </div>

              {/* Desktop nav */}
              <div className="sm:hidden flex items-center gap-[30px]">
                {simple ? (
                  <nav className="flex items-center gap-8 lg:gap-5">
                    {SIMPLE_LINKS.map(({ label, href }) => (
                      <Link
                        key={href}
                        href={href}
                        className={`text-[#002e65] font-[18px] font-[600] hover:text-[#0055a4] ${
                          href === "/clinics" ? "lg:hidden" : ""
                        }`}
                      >
                        {label}
                      </Link>
                    ))}
                    <a
                      href={`tel:${PHONE_TEL}`}
                      className="inline-flex items-center gap-2 rounded-full bg-[#004da9] px-4 py-2 text-white font-[600] hover:bg-[#003d8a] whitespace-nowrap"
                    >
                      <Phone className="w-4 h-4" />
                      {PHONE_DISPLAY}
                    </a>
                  </nav>
                ) : (
                  <>
                <nav className="flex items-center gap-8 md:gap-4">
                  {!session && (
                    <Link
                      href="/process"
                      className={`text-[#002e65] font-[18px] font-[600] hover:text-[#0055a4] ${
                        pathname === "/process" ? "text-[#004da9] border-b-[3px] border-[#004da9]" : ""
                      }`}
                    >
                      The Process
                    </Link>
                  )}

                  <Link
                    href={trainingHref}
                    className={`text-[#002e65] font-[18px] font-[600] hover:text-[#0055a4] flex items-center gap-2 ${
                      pathname === trainingHref ? "text-[#0055a4] border-b-[3px] border-[#0055a4]" : ""
                    }`}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 3L1 9L12 15L21 10.09V17H23V9M5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18Z"/>
                    </svg>
                    <span>Training</span>
                  </Link>
                  <div className="w-px h-5 bg-[#002e65]"></div>
                  
                  {/* Resources Link — clinic/admin areas only */}
                  {showResources && (
                    <>
                      <Link
                        href="/resources"
                        className="text-[#002e65] font-[18px] font-[600] hover:text-[#0055a4] flex items-center gap-2"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 3H5C3.89 3 3 3.89 3 5V19A2 2 0 0 0 5 21H19A2 2 0 0 0 21 19V5C21 3.89 20.1 3 19 3M19 19H5V5H19V19M17 17H7V7H17V17M15 15H9V9H15V15M13 13H11V11H13V13Z"/>
                        </svg>
                        <span>Resources</span>
                      </Link>

                      <div className="w-px h-5 bg-[#002e65]"></div>
                    </>
                  )}

                  {/* Blog Link */}
                  <Link
                    href="/blog"
                    className={`text-[#002e65] font-[18px] font-[600] hover:text-[#0055a4] flex items-center gap-2 ${
                      pathname?.startsWith("/blog") ? "text-[#0055a4] border-b-[3px] border-[#0055a4]" : ""
                    }`}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6zM8 12h8v2H8v-2zm0 4h8v2H8v-2zm0-8h5v2H8V8z"/>
                    </svg>
                    <span>Blog</span>
                  </Link>

                  <div className="w-px h-5 bg-[#002e65]"></div>
                  {/* Show Home link on dashboard pages, Dashboard link on other pages */}
                  {session && (pathname === "/dashboard" || pathname === "/clinicDashboard") ? (
                    <Link
                      href="/"
                      className={`text-[#002e65] font-[18px] font-[600] hover:text-[#0055a4] ${pathname === "/" ? "text-[#0055a4] border-b-[3px] border-[#0055a4]" : ""}`}
                    >
                      Home
                    </Link>
                  ) : session ? (
                    <Link
                      href={session.user.role === "admin" ? "/dashboard" : "/clinicDashboard"}
                      className={`text-[#002e65] font-[18px] font-[600] hover:text-[#0055a4] ${(pathname === "/dashboard" || pathname === "/clinicDashboard") ? "text-[#0055a4] border-b-[3px] border-[#0055a4]" : ""}`}
                    >
                      Dashboard
                    </Link>
                  ) : null}
                  {status === "loading" && !sessionTimeout ? (
                    <div className="flex items-center gap-2 text-[#002e65] font-[18px] font-[600]">
                      <LoadingSpinner size="sm" />
                      <span>Loading...</span>
                    </div>
                  ) : status === "loading" && sessionTimeout ? (
                    <div className="flex items-center gap-2 text-[#002e65] font-[18px] font-[600]">
                      <button 
                        onClick={update} 
                        className="text-[#0055a4] hover:text-[#004080] underline"
                      >
                        Retry
                      </button>
                    </div>
                  ) : session ? (
                    <div className="relative" ref={dropdownRef}>
                      <button
                        onClick={() => setShowDropdown(prev => !prev)}
                        className="text-[#002e65] font-[18px] font-[600] hover:text-[#0055a4] flex items-center gap-1 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full text-white text-sm font-semibold shadow-md">
                            {userName.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-bold text-[#002e65] hover:text-[#0055a4] transition-colors">
                            {userName}
                          </span>
                        </div>
                        <svg 
                          className={`w-4 h-4 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showDropdown && (
                        <div className="absolute right-0 mt-3 bg-white shadow-xl rounded-xl border border-gray-100 z-50 min-w-[200px] overflow-hidden">
                          {/* User info section */}
                          <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100">
                            <p className="text-sm font-medium text-gray-900 truncate">{userName}</p>
                            <p className="text-xs text-gray-500 capitalize">{session.user.role}</p>
                          </div>
                          
                          {/* Menu items */}
                          <div className="py-2">
                            {(pathname === "/dashboard" || pathname === "/clinicDashboard") ? (
                              <Link
                                href="/"
                                onClick={() => setShowDropdown(false)}
                                className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                </svg>
                                Home
                              </Link>
                            ) : (
                              <Link
                                href={
                                  session.user.role === "admin"
                                    ? "/dashboard"
                                    : "/clinicDashboard"
                                }
                                onClick={() => setShowDropdown(false)}
                                className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                Dashboard
                              </Link>
                            )}
                            
                            <Link
                              href="/profile"
                              onClick={() => setShowDropdown(false)}
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              Profile
                            </Link>
                            
                            <div className="border-t border-gray-100 my-1"></div>
                            
                            <button
                              onClick={handleLogout}
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors w-full text-left"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                              </svg>
                              Sign out
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-4">
                      <Link
                        href="/login"
                        className="bg-[#0055a4] text-white px-6 py-2 rounded-full font-medium hover:bg-[#004080] transition-colors text-base"
                      >
                        Login
                      </Link>
                    </div>
                  )}
                </nav>

                <Link
                  href="/"
                  className={`text-[#002e65] font-[18px] font-[600] hover:text-[#0055a4] ${
                    pathname === "/" || pathname?.startsWith("/owners") ? "text-[#004da9] border-b-[3px] border-[#004da9]" : ""
                  }`}
                >
                  Pet Owners
                </Link>

                {!session && (
                  <Link
                    href="/clinics"
                    className={`text-[#002e65] font-[18px] font-[600] hover:text-[#0055a4] ${
                      pathname?.startsWith("/clinics") ? "text-[#004da9] border-b-[3px] border-[#004da9]" : ""
                    }`}
                  >
                    For Vet Clinics
                  </Link>
                )}

                <Link
                  href="/contact"
                  className="text-[#002e65] font-[18px] font-[600] hover:text-[#0055a4]"
                >
                  Contact
                </Link>
                  </>
                )}
              </div>
            </div>


            {/* Mobile nav menu */}
            {isMobileMenuOpen && (
              <div className="hidden sm:block absolute w-full left-0 top-0 text-left bg-[#000f22] shadow-md px-[20px] py-[30px]">
                <button onClick={navigateToHome} className="relative mb-[45px] block">
                  <Image src="/icons/logo-rgb-reversed.svg" alt="PetAftercare" className="w-[160px]" width={250} height={60} priority />
                </button>
                <div className="flex flex-wrap gap-[30px]">
                  {simple ? (
                    <>
                      <a
                        href="/#arrange"
                        onClick={(e) => goToSection(e, "/#arrange")}
                        className="w-full rounded-full bg-[#004da9] px-5 py-3 text-center text-white text-[17px] font-[600]"
                      >
                        Arrange care
                      </a>
                      <a
                        href={`tel:${PHONE_TEL}`}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-white text-[17px] font-[600] ring-1 ring-white/40"
                      >
                        <Phone className="w-4 h-4" />
                        Call {PHONE_DISPLAY}
                      </a>
                      {SIMPLE_LINKS.filter((l) => l.href.startsWith("/#")).map(({ label, href }) => (
                        <a
                          key={href}
                          href={href}
                          onClick={(e) => goToSection(e, href)}
                          className="text-[#fff] w-full text-left text-[18px] font-[600] hover:text-[#8fb4e0]"
                        >
                          {label}
                        </a>
                      ))}
                      <div className="w-full border-t border-white/15 pt-6 flex flex-col gap-4">
                        {SIMPLE_SECONDARY_LINKS.map(({ label, href }) => (
                          <Link
                            key={href}
                            href={href}
                            onClick={closeMenu}
                            className="text-white/75 text-[15px] font-[600] hover:text-white"
                          >
                            {label}
                          </Link>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                  {!session && (
                    <Link href="/process" onClick={closeMenu} className="w-full block text-white text-[16px] font-semibold hover:text-[#0055a4]">
                      The Process
                    </Link>
                  )}
                  {/* Show Home link on dashboard pages, Dashboard link on other pages */}
                  {session && (pathname === "/dashboard" || pathname === "/clinicDashboard") ? (
                    <Link
                      href="/"
                      onClick={closeMenu}
                      className="text-[#fff] w-full text-left font-[18px] font-[600] hover:text-[#0055a4]"
                    >
                      Home
                    </Link>
                  ) : session ? (
                    <Link
                      href={session.user.role === "admin" ? "/dashboard" : "/clinicDashboard"}
                      onClick={closeMenu}
                      className="text-[#fff] w-full text-left font-[18px] font-[600] hover:text-[#0055a4]"
                    >
                      Dashboard
                    </Link>
                  ) : null}
                  
                  {/* Mobile Resources Link — clinic/admin areas only */}
                  {showResources && (
                    <Link href="/resources" onClick={closeMenu} className="w-full block text-white text-[16px] font-semibold hover:text-[#0055a4] flex items-center gap-2">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 3H5C3.89 3 3 3.89 3 5V19A2 2 0 0 0 5 21H19A2 2 0 0 0 21 19V5C21 3.89 20.1 3 19 3M19 19H5V5H19V19M17 17H7V7H17V17M15 15H9V9H15V15M13 13H11V11H13V13Z"/>
                      </svg>
                      <span>Resources</span>
                    </Link>
                  )}

                  {/* Mobile Blog Link */}
                  <Link href="/blog" onClick={closeMenu} className="w-full block text-white text-[16px] font-semibold hover:text-[#0055a4] flex items-center gap-2">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6zM8 12h8v2H8v-2zm0 4h8v2H8v-2zm0-8h5v2H8V8z"/>
                    </svg>
                    <span>Blog</span>
                  </Link>
                  
                  <div className="w-full h-[1px] bg-[#fff]"></div>
                  {status === "loading" && !sessionTimeout ? (
                    <div className="flex items-center gap-2 text-white font-semibold text-[16px]">
                      <LoadingSpinner size="sm" />
                      <span>Loading...</span>
                    </div>
                  ) : status === "loading" && sessionTimeout ? (
                    <div className="flex items-center gap-2 text-white font-semibold text-[16px]">
                      <button 
                        onClick={() => update()} 
                        className="text-[#0055a4] hover:text-[#004080] underline"
                      >
                        Retry
                      </button>
                    </div>
                  ) : session ? (
                    <>
                      <div className="w-full">
                        <p className="text-white font-semibold text-[16px]">{userName}</p>
                        <p className="text-white text-sm opacity-75 capitalize">{session.user.role}</p>
                      </div>
                      {(pathname === "/dashboard" || pathname === "/clinicDashboard") ? (
                        <Link
                          href="/"
                          onClick={closeMenu}
                          className="text-[#fff] w-full text-left font-[18px] font-[600] hover:text-[#0055a4]"
                        >
                          Home
                        </Link>
                      ) : (
                        <Link
                          href={
                            session.user.role === "admin"
                              ? "/dashboard"
                              : "/clinicDashboard"
                          }
                          onClick={closeMenu}
                          className="text-[#fff] w-full text-left font-[18px] font-[600] hover:text-[#0055a4]"
                        >
                          Dashboard
                        </Link>
                      )}
                      <Link
                        href="/profile"
                        onClick={closeMenu}
                        className="text-[#fff] w-full text-left font-[18px] font-[600] hover:text-[#0055a4]"
                      >
                        Profile
                      </Link>
                      <button
                        onClick={() => {
                          handleLogout()
                          closeMenu()
                        }}
                        className="text-[#fff] w-full text-left font-[18px] font-[600] hover:text-[#0055a4]"
                      >
                        Sign out
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        href="/login"
                        onClick={closeMenu}
                        className="text-[#fff] w-full text-left font-[18px] font-[600] hover:text-[#0055a4]"
                      >
                        Login
                      </Link>
                    </>
                  )}
                  
                  <div className="w-full h-[1px] bg-[#fff]"></div>
                  <Link
                    href="/"
                    onClick={closeMenu}
                    className="text-[#fff] w-full text-left font-[18px] font-[600] hover:text-[#0055a4]"
                  >
                    Pet Owners
                  </Link>
                  {!session && (
                    <Link
                      href="/clinics"
                      onClick={closeMenu}
                      className="text-[#fff] w-full text-left font-[18px] font-[600] hover:text-[#0055a4]"
                    >
                      For Vet Clinics
                    </Link>
                  )}
                  <Link
                    href="/contact"
                    onClick={closeMenu}
                    className="text-[#fff] w-full text-left font-[18px] font-[600] hover:text-[#0055a4]"
                  >
                    Contact us
                  </Link>
                    </>
                  )}
                </div>
              </div>
            )}
          </MaxWidthWrapper>
        </div>
      </header>

      {/* Track Modal */}
      <TrackModal 
        isOpen={isTrackModalOpen} 
        onClose={() => setIsTrackModalOpen(false)}
      />
    </>
  )
}
